import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface VerifyResult {
  videoUrl: string;
}

const PracticingSafeTechGate: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [name, setName] = useState('');
  const [passcode, setPasscode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim() || !passcode.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const verify = httpsCallable<
        { name: string; passcode: string },
        VerifyResult
      >(functions, 'verifyPracticingSafeTechPasscode');
      const result = await verify({ name: name.trim(), passcode });
      setVideoUrl(result.data.videoUrl);
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Practicing Safe Tech</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your name and the passcode provided by the Marriage
            Ministry to view this lesson.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="Your Name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Passcode"
              type="password"
              fullWidth
              size="small"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!name.trim() || !passcode.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? 'Verifying…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {videoUrl && (
        <Box
          sx={{
            maxWidth: 800,
            mx: 'auto',
            position: 'relative',
            pt: '56.25%',
          }}
        >
          <iframe
            src={videoUrl}
            allow="autoplay"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
              borderRadius: 8,
            }}
            title="Practicing Safe Tech"
          />
        </Box>
      )}
    </Box>
  );
};

export default PracticingSafeTechGate;
